import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import type { GraphQLClient } from "graphql-request"
import { type ReactElement, type ReactNode, useMemo, useState } from "react"
import { AuthProvider } from "~/features/auth/AuthProvider"
import { useAuth } from "~/features/auth/useAuth"
import { StudyEbookProvider } from "~/features/studyEbook/StudyEbookContext"
import { GraphQLClientContext } from "~/shared/api/GraphQLClientContext"
import { createGraphQLClient } from "~/shared/api/graphqlClient"
import { createQueryClient } from "~/shared/api/queryClient"
import { ThemeModeProvider } from "~/shared/theme/ThemeModeProvider"

/**
 * Reads the token through a ref-free closure so the client is built once but
 * always sees the current token — rebuilding it on every token change would
 * invalidate every in-flight request.
 */
const GraphQLClientProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { accessToken, clearToken } = useAuth()
  const [tokenBox] = useState<{ current: string | undefined }>(() => ({ current: accessToken }))
  tokenBox.current = accessToken

  const client = useMemo<GraphQLClient>(
    () =>
      createGraphQLClient({
        getAccessToken: () => tokenBox.current,
        onUnauthorized: clearToken,
      }),
    [tokenBox, clearToken],
  )

  return <GraphQLClientContext.Provider value={client}>{children}</GraphQLClientContext.Provider>
}

interface AppProvidersProps {
  readonly children: ReactNode
  /**
   * True only for the standalone entry, which owns `<html>` and so may theme it.
   * The federated entry leaves the host's document alone — see `ThemeModeProvider`.
   */
  readonly ownsDocument?: boolean
}

export const AppProviders = ({ children, ownsDocument = false }: AppProvidersProps): ReactElement => {
  const [queryClient] = useState(createQueryClient)

  return (
    <ThemeModeProvider ownsDocument={ownsDocument}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <GraphQLClientProvider>
            {/*
              Above the router, not inside a page: the study detail screen and the
              library grid both ask which ebook accompanies a study, and one index
              over one drained ebook list serves both. Its queries are disabled
              until sign-in, so the auth callback routes cost nothing.
            */}
            <StudyEbookProvider>{children}</StudyEbookProvider>
          </GraphQLClientProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </ThemeModeProvider>
  )
}
